import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import FilterBar, { type FilterState } from "@/pages/category/components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { filterMedia } from "@/utils/media";
import { slugToType, typeLabels } from "@/utils/labels";

const PAGE_SIZE = 12;

const defaultFilters: FilterState = {
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "最新",
};

export default function CategoryPage() {
  const { type } = useParams();
  const activeType = slugToType(type);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  const categoryInfo = categories.find((cat) => cat.key === activeType);
  const title = categoryInfo ? categoryInfo.name : "全部影视";
  const description = categoryInfo
    ? categoryInfo.description
    : "汇集电影、电视剧、综艺等各类影视作品，支持按地区、年份、类型筛选，快速找到想看的内容。";

  const isInvalidType = Boolean(type) && activeType === "all";

  usePageMeta({
    title: isInvalidType
      ? "影视分类 - 17C.COM"
      : `${title} - 影视分类 - 17C.COM`,
    description: `${description.slice(0, 60)}... 17C.COM 影视分类频道，支持多条件筛选与排序。`,
    keywords: `${title},影视分类,${categories.map((c) => c.name).join(",")},17C.COM`,
  });

  const results = useMemo(
    () => filterMedia({ ...filters, type: activeType }),
    [filters, activeType],
  );

  useEffect(() => {
    setPage(1);
  }, [filters, activeType]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pagedResults = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleReset = () => setFilters(defaultFilters);

  const tabs = [
    { key: "all", name: "全部", to: "/category" },
    ...categories.map((cat) => ({ key: cat.key, name: cat.name, to: `/category/${cat.key}` })),
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视分类", to: "/category" },
          ...(activeType !== "all" ? [{ label: typeLabels[activeType] }] : []),
        ]}
      />

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          {title}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          {description}
        </p>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {tabs.map((tab) => (
          <Link
            key={tab.key}
            to={tab.to}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              tab.key === activeType
                ? "bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {tab.name}
          </Link>
        ))}
      </div>

      <div className="mb-6">
        <FilterBar value={filters} onChange={setFilters} onReset={handleReset} />
      </div>

      <div className="mb-4 text-sm text-foreground-500">
        共找到 <span className="font-bold text-primary-600">{results.length}</span> 部作品
        {totalPages > 1 && (
          <span className="ml-2">
            · 第 {page}/{totalPages} 页
          </span>
        )}
      </div>

      {pagedResults.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {pagedResults.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-8">
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </div>
        </>
      ) : (
        <EmptyState
          icon="ri-search-eye-line"
          title="未找到相关影视作品"
          description="换个筛选条件试试，或重置后查看全部内容。"
          action={
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              重置筛选
            </button>
          }
        />
      )}
    </div>
  );
}